import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Outlet } from 'react-router-dom';
import {
  LayoutDashboard, ShoppingBag, Package, Tag, Star,
  Image, Settings, BarChart2, LogOut, ChevronDown,
  Menu as MenuIcon, X, List, PlusCircle, Layers
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useSettings } from '../../context/SettingsContext';
import { toast } from 'sonner';

const navItems = [
  { label: 'Dashboard', href: '/admin/dashboard', icon: LayoutDashboard },
  { label: 'Orders', href: '/admin/orders', icon: ShoppingBag },
  {
    label: 'Products', icon: Package, href: '/admin/products',
    children: [
      { label: 'All Products', href: '/admin/products', icon: List },
      { label: 'Add New', href: '/admin/products/new', icon: PlusCircle },
    ]
  },
  { label: 'Categories', href: '/admin/categories', icon: Tag },
  { label: 'Coupons', href: '/admin/coupons', icon: Tag },
  { label: 'Reviews', href: '/admin/reviews', icon: Star },
  { label: 'Stock Management', href: '/admin/stock', icon: Layers },
  { label: 'Media & Branding', href: '/admin/media', icon: Image },
  { label: 'Settings', href: '/admin/settings', icon: Settings },
  { label: 'Reports', href: '/admin/reports', icon: BarChart2 },
];

const AdminLayout = () => {
  const { admin, logout } = useAuth();
  const { settings } = useSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openSub, setOpenSub] = useState('Products');

  const handleLogout = () => {
    logout();
    toast.success('Logged out');
    navigate('/admin/login');
  };

  const isActive = (href) => location.pathname === href || (href !== '/admin/dashboard' && location.pathname.startsWith(href));

  const SidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className={`flex items-center ${collapsed ? 'justify-center px-2' : 'gap-2 px-5'} py-5 border-b border-[#ECE0DA]`}>
        {settings.logo ? (
          <img src={settings.logo} alt="Logo" className="h-8 w-auto object-contain" />
        ) : (
          <svg width="32" height="32" viewBox="0 0 36 36" fill="none">
            <circle cx="18" cy="18" r="17" fill="#7A1125" stroke="#ECE0DA" strokeWidth="1"/>
            <ellipse cx="18" cy="19" rx="6" ry="4" fill="rgba(255,255,255,0.5)"/>
            <circle cx="18" cy="15" r="3" fill="white" opacity="0.9"/>
          </svg>
        )}
        {!collapsed && (
          <div>
            <div className="font-playfair font-bold text-[#1F1410] text-sm leading-none">Apni Dhani</div>
            <div className="text-[#7A6A63] text-[9px] tracking-[0.15em] uppercase">— BAKERY —</div>
          </div>
        )}
        <button onClick={() => setCollapsed(!collapsed)} className="ml-auto text-[#7A6A63] hover:text-[#7A1125] p-1 hidden md:block">
          <MenuIcon size={16} />
        </button>
      </div>

      {/* Nav items */}
      <nav className="flex-1 py-4 overflow-y-auto scrollbar-thin">
        {navItems.map(item => (
          <div key={item.label}>
            {item.children ? (
              <div>
                <button
                  onClick={() => setOpenSub(openSub === item.label ? '' : item.label)}
                  className={`w-full flex items-center ${collapsed ? 'justify-center px-2' : 'px-4'} py-2.5 gap-3 text-sm font-medium transition-colors ${
                    isActive(item.href) ? 'text-[#7A1125] bg-[#7A1125]/5' : 'text-[#7A6A63] hover:text-[#1F1410] hover:bg-[#FDF8F3]'
                  }`}
                  data-testid={`sidebar-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon size={18} />
                  {!collapsed && <><span className="flex-1 text-left">{item.label}</span><ChevronDown size={14} className={`transition-transform ${openSub === item.label ? 'rotate-180' : ''}`} /></>}
                </button>
                {!collapsed && openSub === item.label && (
                  <div className="ml-9 border-l border-[#ECE0DA] py-1">
                    {item.children.map(child => (
                      <Link
                        key={child.href}
                        to={child.href}
                        onClick={() => setMobileOpen(false)}
                        className={`flex items-center gap-2 px-3 py-2 text-xs transition-colors ${
                          location.pathname === child.href ? 'text-[#7A1125] font-semibold' : 'text-[#7A6A63] hover:text-[#1F1410]'
                        }`}
                        data-testid={`sidebar-${child.label.toLowerCase().replace(' ', '-')}`}
                      >
                        <child.icon size={14} />{child.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link
                to={item.href}
                onClick={() => setMobileOpen(false)}
                className={`flex items-center ${collapsed ? 'justify-center px-2' : 'px-4'} py-2.5 gap-3 text-sm font-medium transition-colors ${
                  isActive(item.href) ? 'text-[#7A1125] bg-[#7A1125]/5 font-semibold border-r-2 border-[#7A1125]' : 'text-[#7A6A63] hover:text-[#1F1410] hover:bg-[#FDF8F3]'
                }`}
                data-testid={`sidebar-${item.label.toLowerCase().replace(/ &/g, '').replace(/ /g, '-')}`}
              >
                <item.icon size={18} />
                {!collapsed && <span>{item.label}</span>}
              </Link>
            )}
          </div>
        ))}
      </nav>

      {/* Admin footer */}
      <div className={`border-t border-[#ECE0DA] ${collapsed ? 'py-4 px-2' : 'p-4'}`}>
        {!collapsed && (
          <div className="flex items-center gap-2 mb-3">
            <div className="w-8 h-8 rounded-full bg-[#7A1125] flex items-center justify-center text-white text-xs font-bold">
              {(admin?.name || 'A')[0]}
            </div>
            <div>
              <p className="text-xs font-semibold text-[#1F1410]">{admin?.name || 'Admin'}</p>
              <p className="text-[9px] text-[#7A6A63]">Administrator</p>
            </div>
          </div>
        )}
        <button
          onClick={handleLogout}
          className={`flex items-center ${collapsed ? 'justify-center' : 'gap-2'} w-full py-2 px-3 text-sm text-[#7A6A63] hover:text-[#C0392B] hover:bg-red-50 rounded-lg transition-colors`}
          data-testid="admin-logout-button"
        >
          <LogOut size={16} />
          {!collapsed && 'Logout'}
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex h-screen bg-[#FDF8F3] overflow-hidden">
      {/* Desktop sidebar */}
      <div className={`hidden md:flex flex-col admin-sidebar transition-all duration-300 shrink-0 ${collapsed ? 'w-16' : 'w-56'}`}>
        <SidebarContent />
      </div>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-50 flex">
          <div className="w-64 bg-white admin-sidebar flex flex-col"><SidebarContent /></div>
          <div className="flex-1 bg-black/40" onClick={() => setMobileOpen(false)} />
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile header */}
        <div className="md:hidden flex items-center justify-between px-4 py-3 bg-white border-b border-[#ECE0DA]">
          <button onClick={() => setMobileOpen(true)} className="text-[#1F1410] p-1"><MenuIcon size={20} /></button>
          <span className="font-playfair font-bold text-[#1F1410] text-sm">Admin Panel</span>
          <div className="w-7" />
        </div>
        <div className="flex-1 overflow-auto p-0">
          <Outlet />
        </div>
      </div>
    </div>
  );
};

export default AdminLayout;
